// ── Avatar Sprite Data ──────────────────────────────────────────────────
//
// Palette index mapping:
//   0 = transparent
//   1 = bodyPrimary
//   2 = bodySecondary
//   3 = eyeColor
//   4 = mouthColor
//   5 = accessoryColor
//
// All base sprites are 14×14 grids.
// Parts (eyes, mouth, accessories) are smaller overlay grids.

import type {
  SpriteDefinition,
  PartDefinition,
  BackgroundDefinition,
  PalettePreset,
} from './avatarTypes';

// ── Helper ──────────────────────────────────────────────────────────────

/** Shorthand — parse compact row strings into number arrays */
function g(rows: string[]): number[][] {
  return rows.map(r => r.split('').map(Number));
}

// ── Base Entity Sprites (14×14) ─────────────────────────────────────────

export const BASE_SPRITES: SpriteDefinition[] = [
  // ── Malware ─────────────────────────────────────────────────────────
  {
    id: 'virus',
    name: 'Virus',
    category: 'malware',
    grid: g([
      '00000210200000',
      '00002010020000',
      '00001111100000',
      '00011111110000',
      '20111111111020',
      '01111111111100',
      '00111111111100',
      '00111111111000',
      '01111111111100',
      '20111111111020',
      '00011111110000',
      '00001111100000',
      '00002010020000',
      '00000210200000',
    ]),
    anchorPoints: { eyes: [4, 4], mouth: [7, 5], accessory: [1, 4] },
  },
  {
    id: 'worm',
    name: 'Worm',
    category: 'malware',
    grid: g([
      '00000000000000',
      '00000111100000',
      '00011111110000',
      '00111111111000',
      '00111111111000',
      '00011111110000',
      '00001111100000',
      '00011111100000',
      '00111111000000',
      '01111110000000',
      '01111110000000',
      '00111111000000',
      '00011111100000',
      '00000111000000',
    ]),
    anchorPoints: { eyes: [3, 4], mouth: [5, 5], accessory: [1, 5] },
  },
  {
    id: 'trojan',
    name: 'Trojan',
    category: 'malware',
    grid: g([
      '00001100110000',
      '00011100111000',
      '00111111111100',
      '00111111111100',
      '01111111111110',
      '01111111111110',
      '01111111111110',
      '01111111111110',
      '00111111111100',
      '00111111111100',
      '00011111111000',
      '00011100111000',
      '00011100111000',
      '00001100110000',
    ]),
    anchorPoints: { eyes: [4, 4], mouth: [7, 5], accessory: [0, 4] },
  },
  {
    id: 'ransomware',
    name: 'Ransomware',
    category: 'malware',
    grid: g([
      '00000550000000',
      '00005555000000',
      '00055005500000',
      '00055005500000',
      '00555555550000',
      '01111111111000',
      '01111111111000',
      '01111111111000',
      '01111111111000',
      '01111111111000',
      '01111111111000',
      '01111111111000',
      '01111111111000',
      '00111111110000',
    ]),
    anchorPoints: { eyes: [7, 3], mouth: [10, 4], accessory: [0, 4] },
  },
  {
    id: 'rootkit',
    name: 'Rootkit',
    category: 'malware',
    grid: g([
      '00000000000000',
      '00000110000000',
      '00001111000000',
      '00011221100000',
      '00112222110000',
      '01122222211000',
      '01122222211000',
      '11222222221100',
      '11222222221100',
      '01122222211000',
      '00112222110000',
      '00011221100000',
      '00001111000000',
      '00000110000000',
    ]),
    anchorPoints: { eyes: [5, 4], mouth: [8, 5], accessory: [1, 4] },
  },
  {
    id: 'spyware',
    name: 'Spyware',
    category: 'malware',
    grid: g([
      '00000000000000',
      '00001111100000',
      '00011111110000',
      '00111111111000',
      '01111111111100',
      '01111111111100',
      '01111111111100',
      '00111111111000',
      '00011111110000',
      '00001111100000',
      '00000111000000',
      '00000111000000',
      '00000010000000',
      '00000010000000',
    ]),
    anchorPoints: { eyes: [4, 4], mouth: [7, 5], accessory: [0, 4] },
  },
  {
    id: 'adware',
    name: 'Adware',
    category: 'malware',
    grid: g([
      '00000000000000',
      '00055555550000',
      '00511111115000',
      '05111111111500',
      '05111111111500',
      '05111111111500',
      '05111111111500',
      '05111111111500',
      '05111111111500',
      '00511111115000',
      '00055555550000',
      '00000110000000',
      '00000110000000',
      '00000000000000',
    ]),
    anchorPoints: { eyes: [4, 4], mouth: [7, 5], accessory: [0, 5] },
  },
  {
    id: 'botnet',
    name: 'Botnet',
    category: 'malware',
    grid: g([
      '00000000000000',
      '00002002000000',
      '00011111100000',
      '00111111110000',
      '01111111111000',
      '01111111111000',
      '01111111111000',
      '01111111111000',
      '00111111110000',
      '00011111100000',
      '02011010110200',
      '00011010110000',
      '00010000100000',
      '00010000100000',
    ]),
    anchorPoints: { eyes: [4, 3], mouth: [7, 4], accessory: [0, 4] },
  },
  {
    id: 'keylogger',
    name: 'Keylogger',
    category: 'malware',
    grid: g([
      '00000000000000',
      '00000110000000',
      '00001111000000',
      '00011111100000',
      '00011111100000',
      '00001111000000',
      '01111111111100',
      '01111111111100',
      '01551551551100',
      '01111111111100',
      '01551551551100',
      '01111111111100',
      '01111111111100',
      '00000000000000',
    ]),
    anchorPoints: { eyes: [3, 4], mouth: [5, 5], accessory: [0, 4] },
  },
  {
    id: 'rat',
    name: 'RAT',
    category: 'malware',
    grid: g([
      '01100000001100',
      '01110000011100',
      '01111111111100',
      '11111111111110',
      '11111111111110',
      '11111111111110',
      '01111111111100',
      '00111111111000',
      '00011111110000',
      '00001111100000',
      '00000111000000',
      '00000011000000',
      '00000001100000',
      '00000000110000',
    ]),
    anchorPoints: { eyes: [3, 3], mouth: [6, 4], accessory: [0, 3] },
  },

  // ── Threat Actors ───────────────────────────────────────────────────
  {
    id: 'hacker',
    name: 'Hacker',
    category: 'actor',
    grid: g([
      '00011111110000',
      '00111111111000',
      '00111111111000',
      '00011111110000',
      '00011111110000',
      '00111111111000',
      '00111111111000',
      '00111111111000',
      '00011111110000',
      '01111111111100',
      '01111111111100',
      '01111001111100',
      '01110000111100',
      '00110000011000',
    ]),
    anchorPoints: { eyes: [5, 4], mouth: [7, 5], accessory: [0, 3] },
  },
  {
    id: 'apt-operative',
    name: 'APT Operative',
    category: 'actor',
    grid: g([
      '00222222220000',
      '02222222222000',
      '02211111122000',
      '00111111110000',
      '00111111110000',
      '00111111110000',
      '00111111110000',
      '00011111100000',
      '00111111110000',
      '01112222111000',
      '01122222211000',
      '01122222211000',
      '00112002110000',
      '00110000110000',
    ]),
    anchorPoints: { eyes: [4, 3], mouth: [6, 4], accessory: [0, 2] },
  },
  {
    id: 'script-kiddie',
    name: 'Script Kiddie',
    category: 'actor',
    grid: g([
      '00055555500000',
      '00555555550000',
      '00511111150000',
      '00111111110000',
      '00111111110000',
      '00111111110000',
      '00111111110000',
      '00011111100000',
      '00011111100000',
      '00111111110000',
      '00111111110000',
      '00111001110000',
      '00110000110000',
      '00110000110000',
    ]),
    anchorPoints: { eyes: [4, 3], mouth: [6, 4], accessory: [0, 3] },
  },
  {
    id: 'hacktivist',
    name: 'Hacktivist',
    category: 'actor',
    grid: g([
      '00000000000000',
      '00011111110000',
      '00111111111000',
      '00111111111000',
      '00555555555000',
      '00111111111000',
      '00111111111000',
      '00011111110000',
      '00011111100000',
      '00111111110000',
      '00111111110000',
      '00111001110000',
      '00110000110000',
      '00110000110000',
    ]),
    anchorPoints: { eyes: [2, 4], mouth: [6, 4], accessory: [0, 3] },
  },
  {
    id: 'insider',
    name: 'Insider',
    category: 'actor',
    grid: g([
      '00000000000000',
      '00011111100000',
      '00111111110000',
      '00111111110000',
      '00111111110000',
      '00111111110000',
      '00011111100000',
      '00011111100000',
      '01122222211000',
      '01122222211000',
      '01122222211000',
      '01122002211000',
      '00110000110000',
      '00110000110000',
    ]),
    anchorPoints: { eyes: [3, 4], mouth: [5, 4], accessory: [0, 3] },
  },

  // ── Cyber Creatures ─────────────────────────────────────────────────
  {
    id: 'digital-ghost',
    name: 'Digital Ghost',
    category: 'creature',
    grid: g([
      '00000000000000',
      '00001111100000',
      '00011111110000',
      '00111111111000',
      '01111111111100',
      '01111111111100',
      '01111111111100',
      '01111111111100',
      '01111111111100',
      '01111111111100',
      '01111111111100',
      '01101110110100',
      '01001010010100',
      '00000000000000',
    ]),
    anchorPoints: { eyes: [4, 4], mouth: [7, 5], accessory: [0, 4] },
  },
  {
    id: 'code-golem',
    name: 'Code Golem',
    category: 'creature',
    grid: g([
      '00000000000000',
      '00011111110000',
      '00111111111000',
      '00111111111000',
      '00111111111000',
      '00011111110000',
      '00022222200000',
      '00222222220000',
      '02222222222000',
      '02222222222000',
      '00222222220000',
      '00022002200000',
      '00022002200000',
      '00022002200000',
    ]),
    anchorPoints: { eyes: [3, 4], mouth: [5, 5], accessory: [0, 3] },
  },
  {
    id: 'firewall-guardian',
    name: 'Firewall Guardian',
    category: 'creature',
    grid: g([
      '00000550000000',
      '00005555000000',
      '00011111100000',
      '00111111110000',
      '00111111110000',
      '00111111110000',
      '00111111110000',
      '00011111100000',
      '01155555511000',
      '01555555551000',
      '01555555551000',
      '01555555551000',
      '00155555510000',
      '00015555100000',
    ]),
    anchorPoints: { eyes: [4, 4], mouth: [6, 4], accessory: [0, 3] },
  },
  {
    id: 'data-leech',
    name: 'Data Leech',
    category: 'creature',
    grid: g([
      '00000000000000',
      '00001111000000',
      '00011111100000',
      '00111111110000',
      '00111111110000',
      '00111111110000',
      '00011111100000',
      '00011111100000',
      '00001111000000',
      '00001111000000',
      '00001111000000',
      '00000110000000',
      '00000110000000',
      '00000000000000',
    ]),
    anchorPoints: { eyes: [3, 4], mouth: [5, 4], accessory: [0, 4] },
  },
  {
    id: 'pixel-bug',
    name: 'Pixel Bug',
    category: 'creature',
    grid: g([
      '00000000000000',
      '00200000020000',
      '00020000200000',
      '00011111100000',
      '02111111112000',
      '00111111110000',
      '02111111112000',
      '00111111110000',
      '02111111112000',
      '00011111100000',
      '00001111000000',
      '00010000100000',
      '00100000010000',
      '00000000000000',
    ]),
    anchorPoints: { eyes: [4, 4], mouth: [7, 5], accessory: [0, 4] },
  },
  // ── Additional Malware (to reach 25 total) ──────────────────────────
  {
    id: 'backdoor',
    name: 'Backdoor',
    category: 'malware',
    grid: g([
      '00000000000000',
      '00111111110000',
      '01111111111000',
      '01100000011000',
      '01100000011000',
      '01100001111000',
      '01100001111000',
      '01100000011000',
      '01100000011000',
      '01111111111000',
      '00111111110000',
      '00001111000000',
      '00000110000000',
      '00000000000000',
    ]),
    anchorPoints: { eyes: [4, 3], mouth: [7, 4], accessory: [0, 3] },
  },
  {
    id: 'dropper',
    name: 'Dropper',
    category: 'malware',
    grid: g([
      '00000110000000',
      '00000110000000',
      '00001111000000',
      '00001111000000',
      '00011111100000',
      '00011111100000',
      '00111111110000',
      '00111111110000',
      '01111111111000',
      '01111111111000',
      '00111111110000',
      '00001111000000',
      '00000110000000',
      '00000000000000',
    ]),
    anchorPoints: { eyes: [5, 4], mouth: [8, 4], accessory: [0, 4] },
  },
  {
    id: 'wiper',
    name: 'Wiper',
    category: 'malware',
    grid: g([
      '00000000000000',
      '00000220000000',
      '00002222000000',
      '00022112200000',
      '00211111120000',
      '02111111112000',
      '02111111112000',
      '02111111112000',
      '02111111112000',
      '00211111120000',
      '00022112200000',
      '00002222000000',
      '00000220000000',
      '00000000000000',
    ]),
    anchorPoints: { eyes: [5, 4], mouth: [8, 5], accessory: [0, 4] },
  },
  {
    id: 'cryptominer',
    name: 'Cryptominer',
    category: 'malware',
    grid: g([
      '00000000000000',
      '00011111100000',
      '00111111110000',
      '01115511111000',
      '01155011111000',
      '01150001111000',
      '01155011111000',
      '01115511111000',
      '01111111111000',
      '00111111110000',
      '00011111100000',
      '00001551000000',
      '00001551000000',
      '00000000000000',
    ]),
    anchorPoints: { eyes: [3, 5], mouth: [7, 5], accessory: [0, 4] },
  },
  {
    id: 'logic-bomb',
    name: 'Logic Bomb',
    category: 'malware',
    grid: g([
      '00000002200000',
      '00000022000000',
      '00000110000000',
      '00011111100000',
      '00111111110000',
      '01111111111000',
      '01111111111000',
      '01111111111000',
      '01111111111000',
      '01111111111000',
      '00111111110000',
      '00011111100000',
      '00001111000000',
      '00000000000000',
    ]),
    anchorPoints: { eyes: [6, 4], mouth: [9, 4], accessory: [0, 5] },
  },
];

// ── Eye Parts (small overlay grids) ─────────────────────────────────────

export const EYE_PARTS: PartDefinition[] = [
  {
    id: 'normal',
    name: 'Normal',
    type: 'eyes',
    grid: g([
      '03300330',
      '03300330',
    ]),
  },
  {
    id: 'angry',
    name: 'Angry',
    type: 'eyes',
    grid: g([
      '30000030',
      '03300330',
      '03300330',
    ]),
  },
  {
    id: 'glowing',
    name: 'Glowing',
    type: 'eyes',
    grid: g([
      '33300333',
      '33300333',
      '03300330',
    ]),
  },
  {
    id: 'cyclops',
    name: 'Cyclops',
    type: 'eyes',
    grid: g([
      '00033000',
      '00333000',
      '00033000',
    ]),
  },
  {
    id: 'visor',
    name: 'Visor',
    type: 'eyes',
    grid: g([
      '33333333',
      '33333333',
    ]),
  },
  {
    id: 'x-eyes',
    name: 'X-Eyes',
    type: 'eyes',
    grid: g([
      '30300303',
      '03000030',
      '30300303',
    ]),
  },
  {
    id: 'heart',
    name: 'Heart',
    type: 'eyes',
    grid: g([
      '30300303',
      '33300333',
      '03000030',
    ]),
  },
  {
    id: 'laser',
    name: 'Laser',
    type: 'eyes',
    grid: g([
      '03300330',
      '33333333',
    ]),
  },
  {
    id: 'hollow',
    name: 'Hollow',
    type: 'eyes',
    grid: g([
      '03300330',
      '30000300',
      '03300330',
    ]),
  },
  {
    id: 'spiral',
    name: 'Spiral',
    type: 'eyes',
    grid: g([
      '03300330',
      '30300303',
      '03300330',
    ]),
  },
];

// ── Mouth Parts ─────────────────────────────────────────────────────────

export const MOUTH_PARTS: PartDefinition[] = [
  {
    id: 'smile',
    name: 'Smile',
    type: 'mouth',
    grid: g([
      '40004',
      '04440',
    ]),
  },
  {
    id: 'fangs',
    name: 'Fangs',
    type: 'mouth',
    grid: g([
      '44444',
      '40404',
      '00400',
    ]),
  },
  {
    id: 'zigzag',
    name: 'Zigzag',
    type: 'mouth',
    grid: g([
      '40404',
      '04040',
    ]),
  },
  {
    id: 'open',
    name: 'Open',
    type: 'mouth',
    grid: g([
      '04440',
      '40004',
      '40004',
      '04440',
    ]),
  },
  {
    id: 'flat',
    name: 'Flat',
    type: 'mouth',
    grid: g([
      '44444',
    ]),
  },
  {
    id: 'derp',
    name: 'Derp',
    type: 'mouth',
    grid: g([
      '00044',
      '44440',
    ]),
  },
  {
    id: 'scream',
    name: 'Scream',
    type: 'mouth',
    grid: g([
      '04440',
      '44444',
      '44444',
      '04440',
    ]),
  },
  {
    id: 'byte',
    name: 'Byte',
    type: 'mouth',
    grid: g([
      '44044',
      '04440',
    ]),
  },
];

// ── Accessory Parts ─────────────────────────────────────────────────────

export const ACCESSORY_PARTS: PartDefinition[] = [
  {
    id: 'none',
    name: 'None',
    type: 'accessory',
    grid: [],
  },
  {
    id: 'hacker-hat',
    name: 'Hacker Hat',
    type: 'accessory',
    grid: g([
      '00555500',
      '05555550',
      '55555555',
    ]),
  },
  {
    id: 'hoodie',
    name: 'Hoodie',
    type: 'accessory',
    grid: g([
      '55000055',
      '55000055',
      '55000055',
    ]),
  },
  {
    id: 'shield',
    name: 'Shield',
    type: 'accessory',
    grid: g([
      '0555550',
      '5555555',
      '5555555',
      '0555550',
      '0055500',
      '0005000',
    ]),
  },
  {
    id: 'usb-antenna',
    name: 'USB Antenna',
    type: 'accessory',
    grid: g([
      '050',
      '050',
      '050',
      '555',
    ]),
  },
  {
    id: 'lock',
    name: 'Lock',
    type: 'accessory',
    grid: g([
      '05550',
      '50005',
      '55555',
      '55555',
      '55555',
    ]),
  },
  {
    id: 'chains',
    name: 'Chains',
    type: 'accessory',
    grid: g([
      '50505050',
      '05050505',
    ]),
  },
  {
    id: 'binary-scarf',
    name: 'Binary Scarf',
    type: 'accessory',
    grid: g([
      '555555550',
      '055555555',
    ]),
  },
  {
    id: 'circuit-crown',
    name: 'Circuit Crown',
    type: 'accessory',
    grid: g([
      '5050505',
      '5555555',
      '0555550',
    ]),
  },
  {
    id: 'firewall-cape',
    name: 'Firewall Cape',
    type: 'accessory',
    grid: g([
      '50000005',
      '55000055',
      '55500555',
    ]),
  },
  {
    id: 'keycard',
    name: 'Keycard',
    type: 'accessory',
    grid: g([
      '5555',
      '5005',
      '5555',
    ]),
  },
  {
    id: 'skull-badge',
    name: 'Skull Badge',
    type: 'accessory',
    grid: g([
      '05550',
      '50505',
      '05550',
      '05050',
    ]),
  },
];

// ── Background Patterns (8×8 tiles, repeated) ───────────────────────────

export const BACKGROUND_PATTERNS: BackgroundDefinition[] = [
  {
    id: 'none',
    name: 'None',
    grid: [],
  },
  {
    id: 'circuit-board',
    name: 'Circuit Board',
    grid: g([
      '00000010',
      '00000010',
      '01111110',
      '01000000',
      '01000000',
      '01000001',
      '01111111',
      '00000001',
    ]),
  },
  {
    id: 'binary-rain',
    name: 'Binary Rain',
    grid: g([
      '10001000',
      '00100010',
      '10001000',
      '01000100',
      '00100010',
      '10001000',
      '00010001',
      '01000100',
    ]),
  },
  {
    id: 'hex-grid',
    name: 'Hex Grid',
    grid: g([
      '01111100',
      '10000010',
      '10000010',
      '01111100',
      '00011111',
      '00100001',
      '00100001',
      '00011111',
    ]),
  },
  {
    id: 'network-nodes',
    name: 'Network Nodes',
    grid: g([
      '10000001',
      '01000010',
      '00100100',
      '00011000',
      '00011000',
      '00100100',
      '01000010',
      '10000001',
    ]),
  },
  {
    id: 'firewall-bricks',
    name: 'Firewall Bricks',
    grid: g([
      '11111011',
      '00000000',
      '11101111',
      '00000000',
      '11111011',
      '00000000',
      '11101111',
      '00000000',
    ]),
  },
  {
    id: 'data-stream',
    name: 'Data Stream',
    grid: g([
      '10101010',
      '01010101',
      '10101010',
      '01010101',
      '10101010',
      '01010101',
      '10101010',
      '01010101',
    ]),
  },
  {
    id: 'pixel-stars',
    name: 'Pixel Stars',
    grid: g([
      '00010000',
      '00000000',
      '00000001',
      '00000000',
      '01000000',
      '00000010',
      '00000000',
      '00000000',
    ]),
  },
];

// ── Preset Palettes ─────────────────────────────────────────────────────

export const PALETTE_PRESETS: PalettePreset[] = [
  {
    id: 'rose-pine',
    name: 'Ros\u00e9 Pine',
    colors: {
      bodyPrimary: '#f38ba8',
      bodySecondary: '#eba0ac',
      eyeColor: '#89b4fa',
      mouthColor: '#a6e3a1',
      accessoryColor: '#cba6f7',
      backgroundColor: '#313244',
    },
  },
  {
    id: 'catppuccin',
    name: 'Catppuccin',
    colors: {
      bodyPrimary: '#cba6f7',
      bodySecondary: '#b4befe',
      eyeColor: '#89b4fa',
      mouthColor: '#a6e3a1',
      accessoryColor: '#f9e2af',
      backgroundColor: '#1e1e2e',
    },
  },
  {
    id: 'neon',
    name: 'Neon',
    colors: {
      bodyPrimary: '#ff00ff',
      bodySecondary: '#ff44ff',
      eyeColor: '#00ffff',
      mouthColor: '#00ff41',
      accessoryColor: '#ffff00',
      backgroundColor: '#0a0a2e',
    },
  },
  {
    id: 'earth',
    name: 'Earth',
    colors: {
      bodyPrimary: '#8B7355',
      bodySecondary: '#A0926B',
      eyeColor: '#6B8E23',
      mouthColor: '#D2691E',
      accessoryColor: '#B8860B',
      backgroundColor: '#2F4F2F',
    },
  },
  {
    id: 'ocean',
    name: 'Ocean',
    colors: {
      bodyPrimary: '#4682B4',
      bodySecondary: '#5F9EA0',
      eyeColor: '#00CED1',
      mouthColor: '#20B2AA',
      accessoryColor: '#48D1CC',
      backgroundColor: '#191970',
    },
  },
  {
    id: 'sunset',
    name: 'Sunset',
    colors: {
      bodyPrimary: '#FF6B35',
      bodySecondary: '#FF8C42',
      eyeColor: '#FFD700',
      mouthColor: '#FF4500',
      accessoryColor: '#DC143C',
      backgroundColor: '#2D1B3D',
    },
  },
  {
    id: 'monochrome',
    name: 'Monochrome',
    colors: {
      bodyPrimary: '#CCCCCC',
      bodySecondary: '#999999',
      eyeColor: '#FFFFFF',
      mouthColor: '#666666',
      accessoryColor: '#DDDDDD',
      backgroundColor: '#1A1A1A',
    },
  },
];

// ── Lookup Maps ─────────────────────────────────────────────────────────

export const SPRITES_BY_ID = Object.fromEntries(BASE_SPRITES.map(s => [s.id, s]));
export const EYES_BY_ID = Object.fromEntries(EYE_PARTS.map(p => [p.id, p]));
export const MOUTHS_BY_ID = Object.fromEntries(MOUTH_PARTS.map(p => [p.id, p]));
export const ACCESSORIES_BY_ID = Object.fromEntries(ACCESSORY_PARTS.map(p => [p.id, p]));
export const BACKGROUNDS_BY_ID = Object.fromEntries(BACKGROUND_PATTERNS.map(b => [b.id, b]));
export const PALETTES_BY_ID = Object.fromEntries(PALETTE_PRESETS.map(p => [p.id, p]));
